'use client';

import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';

// Cards across the public home navigate via `router.push` from a custom
// click handler (not `<Link>`), which means Next's automatic
// viewport-based prefetch never fires. This hook returns hover/focus
// handlers that ask the router to prefetch the destination the first
// time the user signals intent — afterwards the navigation feels
// instant because the page chunk and RSC payload are already warm.
//
// `enabled=false` short-circuits for hrefs that can't be prefetched
// (e.g. the backoffice routes, which are client-side only).
export default function usePrefetchOnIntent(href: string, enabled = true) {
  const router = useRouter();
  const prefetched = useRef(false);

  const prefetch = useCallback(() => {
    if (!enabled || prefetched.current || !href) {
      return;
    }
    prefetched.current = true;
    try {
      router.prefetch(href);
    } catch {
      // router.prefetch is best-effort — if Next can't resolve the route
      // (e.g. external rewrites) it throws; ignore and let the click do
      // its job at navigation time.
    }
  }, [router, href, enabled]);

  return {
    onMouseEnter: prefetch,
    onFocus: prefetch,
    onTouchStart: prefetch,
  };
}
